import { Button, Empty, Input, Modal } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";

import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";

const namesMatch = (left: string, right: string) =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

export type DiscoverProviderRow = {
  key: string;
  providerId: string;
  providerName: string;
  subprovider: string;
  iconUrl: string;
  gameCount: number;
  activeCount: number;
  alreadyRegistered: boolean;
};

type DiscoverProvidersPageProps = {
  open: boolean;
  onCancel: () => void;
  discoverSearch: string;
  onDiscoverSearchChange: (value: string) => void;
  hideRegistered: boolean;
  onHideRegisteredChange: (value: boolean) => void;
  isLoadingGames: boolean;
  isError: boolean;
  discoverRows: DiscoverProviderRow[];
  totalCount: number;
  fallbackProviderIcon: string;
  onRegisterClick: (row: DiscoverProviderRow) => void;
};

function DiscoverProvidersPage({
  open,
  onCancel,
  discoverSearch,
  onDiscoverSearchChange,
  hideRegistered,
  onHideRegisteredChange,
  isLoadingGames,
  isError,
  discoverRows,
  totalCount,
  fallbackProviderIcon,
  onRegisterClick,
}: DiscoverProvidersPageProps) {
  // Filtered-of-total when the search box or hide-registered toggle is
  // narrowing ("Showing 4 of 62"); plain total otherwise ("62 providers").
  // The total is the on-screen proof the fetch-all loop paged past the
  // server's 50-row window; the filtered count tracks what's visible.
  const isFiltering = discoverSearch.trim() !== "" || hideRegistered;
  const countLabel = isFiltering
    ? formatString(AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_COUNT_FILTERED, {
        shown: String(discoverRows.length),
        total: String(totalCount),
      })
    : formatString(AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_COUNT, {
        total: String(totalCount),
      });
  return (
    <Modal
      open={open}
      rootClassName="casino-providers-modal-root"
      onCancel={onCancel}
      footer={null}
      width={760}
      closeIcon={<CloseOutlined />}
      className="casino-providers-modal"
      title={
        <span className="casino-providers-modal-title">
          {AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_TITLE}
        </span>
      }
    >
      <div className="casino-providers-modal-subtitle">
        {AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_SUBTITLE}
      </div>

      <Input
        className="casino-providers-modal-input"
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_SEARCH_PLACEHOLDER}
        value={discoverSearch}
        onChange={(event) => onDiscoverSearchChange(event.target.value)}
        allowClear
      />

      <div className="casino-providers-toggle-line">
        <label className="casino-providers-toggle-switch">
          <input
            type="checkbox"
            checked={hideRegistered}
            onChange={(event) => onHideRegisteredChange(event.target.checked)}
            aria-label={AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_HIDE_REGISTERED}
          />
          <span className="casino-providers-toggle-slider"></span>
        </label>
        <span>{AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_HIDE_REGISTERED}</span>
        {!isLoadingGames && !isError && (
          <span className="casino-providers-discover-total">{countLabel}</span>
        )}
      </div>

      <div className="casino-providers-discover-list">
        {isLoadingGames ? (
          <div className="casino-providers-loader">
            <RebetLoader height="72px" width="72px" />
          </div>
        ) : isError ? (
          <div className="casino-providers-empty">
            <Empty description={AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_ERROR} />
          </div>
        ) : discoverRows.length === 0 ? (
          <div className="casino-providers-empty">
            <Empty description={AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_EMPTY} />
          </div>
        ) : (
          discoverRows.map((row) => (
            <div className="casino-providers-discover-row" key={row.key}>
              <div className="casino-providers-row-left">
                <img
                  className="casino-providers-config-logo"
                  src={row.iconUrl || fallbackProviderIcon}
                  alt={row.providerName}
                />
                <span className="casino-providers-row-name">{row.providerName}</span>
                {row.subprovider &&
                  !namesMatch(row.subprovider, row.providerName) && (
                    <span className="casino-providers-via-tag">
                      {AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_VIA_PREFIX}{" "}
                      {row.subprovider}
                    </span>
                  )}
              </div>

              <div className="casino-providers-row-right">
                <span className="casino-providers-discover-count">
                  {row.gameCount} {AppStrings.CASINO_CONFIG_PROVIDERS_GAMES_SUFFIX}
                </span>
                <span
                  className={`casino-providers-status ${
                    row.activeCount > 0
                      ? "casino-providers-status-active"
                      : "casino-providers-status-inactive"
                  }`}
                >
                  {row.activeCount}{" "}
                  {AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_ACTIVE_SUFFIX}
                </span>
                <Button
                  className="casino-providers-outline-btn"
                  disabled={row.alreadyRegistered}
                  onClick={() => onRegisterClick(row)}
                >
                  {row.alreadyRegistered
                    ? AppStrings.CASINO_CONFIG_PROVIDERS_REGISTERED_BUTTON
                    : AppStrings.CASINO_CONFIG_PROVIDERS_REGISTER_BUTTON}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

export default DiscoverProvidersPage;
